import * as React from 'react';

/**
 * @interface LoaderProps
 * @description Define las propiedades del componente Loader.
 */
interface LoaderProps {
  /**
   * @property {string} message - Mensaje personalizado a mostrar durante la carga.
   * @default "Cargando datos..."
   */
  message?: string;
  /**
   * @property {string} subtitle - Subtítulo opcional adicional.
   */
  subtitle?: string;
  /**
   * @property {React.ReactNode} icon - Icono personalizado opcional (por defecto usa spinner).
   */
  icon?: React.ReactNode;
  /**
   * @property {string} className - Clases CSS adicionales para personalización.
   */
  className?: string;
  /**
   * @property {boolean} fullScreen - Si es true, el loader ocupará toda la pantalla con posición fija.
   * @default true
   */
  fullScreen?: boolean;
}

const ORBITAL_DOTS = [
  { position: 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2', color: 'bg-materialidad-rojo-300' },
  { position: 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2', color: 'bg-materialidad-azul-300' },
  { position: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2', color: 'bg-materialidad-azul-200' },
  { position: 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2', color: 'bg-materialidad-azul-100' },
] as const;

const MaterialidadArcSpinner: React.FC = () => (
  <svg
    className="materialidad-loader-arc"
    viewBox="0 0 88 88"
    fill="none"
    aria-hidden="true"
  >
    <circle
      cx="44"
      cy="44"
      r="36"
      stroke="var(--color-materialidad-azul-100)"
      strokeWidth="5"
    />
    <circle
      cx="44"
      cy="44"
      r="36"
      stroke="url(#materialidad-loader-gradient)"
      strokeWidth="5"
      strokeLinecap="round"
      strokeDasharray="72 154"
    />
    <defs>
      <linearGradient
        id="materialidad-loader-gradient"
        x1="8"
        y1="44"
        x2="80"
        y2="44"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="var(--color-materialidad-azul-300)" />
        <stop offset="1" stopColor="var(--color-materialidad-rojo-300)" />
      </linearGradient>
    </defs>
  </svg>
);

/**
 * @component Loader
 * @description Pantalla de carga con animación orbital y paleta de colores Materialidad.
 */
export const Loader: React.FC<LoaderProps> = ({
  message = 'Cargando datos...',
  subtitle,
  icon,
  className = '',
  fullScreen = true,
}) => {
  const baseClasses = fullScreen
    ? 'fixed inset-0 w-screen h-screen z-[9999]'
    : 'min-h-screen w-full relative';

  return (
    <div
      className={`flex flex-col items-center justify-center overflow-hidden bg-background-primary ${baseClasses} ${className}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-gradient-to-br from-background-primary via-materialidad-azul-50/40 to-materialidad-azul-100/25" />
        <div className="materialidad-loader-blob materialidad-loader-blob--azul" />
        <div className="materialidad-loader-blob materialidad-loader-blob--rojo" />
        <div className="materialidad-loader-blob materialidad-loader-blob--center" />
      </div>

      <div className="materialidad-loader-panel relative z-10 flex flex-col items-center gap-7 px-6 py-10 sm:px-10">
        <div className="relative flex h-44 w-44 items-center justify-center">
          <div className="absolute inset-3 rounded-full border border-dashed border-materialidad-azul-200/70 materialidad-loader-orbit-ring" />
          <div className="absolute inset-0 rounded-full bg-white/70 shadow-[0_8px_32px_rgba(0,93,182,0.12)] ring-1 ring-white/80" />
          <div className="relative z-10 flex h-[5.5rem] w-[5.5rem] items-center justify-center">
            {icon ? (
              <div className="text-materialidad-azul-300">{icon}</div>
            ) : (
              <MaterialidadArcSpinner />
            )}
          </div>
          <div className="absolute inset-0 materialidad-loader-orbit-dots">
            {ORBITAL_DOTS.map((dot) => (
              <span
                key={dot.position}
                className={`absolute h-2.5 w-2.5 rounded-full shadow-sm ${dot.color} ${dot.position}`}
              />
            ))}
          </div>
        </div>

        <div className="w-full max-w-md rounded-2xl border border-white/60 bg-white/80 px-6 py-5 text-center shadow-lg shadow-materialidad-azul-300/10 backdrop-blur-md">
          <h3 className="text-lg font-semibold tracking-tight text-materialidad-azul-500 sm:text-xl">
            {message}
          </h3>
          {subtitle && (
            <p className="mt-2 text-sm leading-relaxed text-materialidad-azul-400/95">
              {subtitle}
            </p>
          )}
          <div className="mt-5 h-1 overflow-hidden rounded-full bg-materialidad-azul-50">
            <div className="materialidad-loader-shimmer" />
          </div>
        </div>
      </div>

      <style>{`
        .materialidad-loader-panel {
          animation: materialidad-loader-enter 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .materialidad-loader-blob {
          position: absolute;
          border-radius: 9999px;
          filter: blur(64px);
        }

        .materialidad-loader-blob--azul {
          top: -6rem;
          left: -4rem;
          width: 22rem;
          height: 22rem;
          background: color-mix(in srgb, var(--color-materialidad-azul-100) 55%, transparent);
          animation: materialidad-loader-float 9s ease-in-out infinite;
        }

        .materialidad-loader-blob--rojo {
          right: -5rem;
          bottom: -5rem;
          width: 18rem;
          height: 18rem;
          background: color-mix(in srgb, var(--color-materialidad-rojo-100) 45%, transparent);
          animation: materialidad-loader-float 11s ease-in-out infinite reverse;
        }

        .materialidad-loader-blob--center {
          top: 50%;
          left: 50%;
          width: 14rem;
          height: 14rem;
          transform: translate(-50%, -50%);
          background: color-mix(in srgb, var(--color-materialidad-azul-50) 35%, transparent);
          animation: materialidad-loader-glow 4s ease-in-out infinite;
        }

        .materialidad-loader-arc {
          width: 5.5rem;
          height: 5.5rem;
          animation: materialidad-loader-spin 1.05s linear infinite;
        }

        .materialidad-loader-orbit-ring {
          animation: materialidad-loader-spin 14s linear infinite;
        }

        .materialidad-loader-orbit-dots {
          animation: materialidad-loader-spin 7s linear infinite;
        }

        .materialidad-loader-shimmer {
          width: 42%;
          height: 100%;
          border-radius: 9999px;
          background: linear-gradient(
            90deg,
            var(--color-materialidad-azul-300),
            var(--color-materialidad-azul-200),
            var(--color-materialidad-rojo-300)
          );
          animation: materialidad-loader-shimmer 1.8s ease-in-out infinite;
        }

        @keyframes materialidad-loader-enter {
          from {
            opacity: 0;
            transform: translateY(12px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes materialidad-loader-spin {
          to {
            transform: rotate(1turn);
          }
        }

        @keyframes materialidad-loader-float {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(12px, -16px) scale(1.06);
          }
        }

        @keyframes materialidad-loader-glow {
          0%,
          100% {
            opacity: 0.45;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 0.7;
            transform: translate(-50%, -50%) scale(1.08);
          }
        }

        @keyframes materialidad-loader-shimmer {
          0% {
            transform: translateX(-120%);
          }
          100% {
            transform: translateX(320%);
          }
        }
      `}</style>
    </div>
  );
};
